import { useCombinedRefs, useFileReader } from '../shared';
import { ImagePreviewProps } from './image-preview-props';
import React from 'react';
import { cx } from '@mint/core';
import { noop } from 'lodash';
import styled from 'styled-components';

const ROOT_CLASS_NAME = 'image-preview';

const DEFAULT_WIDTH = 100;

const DEFAULT_HEIGHT = 100;

const cropImage = (
  { height = DEFAULT_HEIGHT, width = DEFAULT_WIDTH }: ImagePreviewProps,
  img: HTMLImageElement,
): [number, number, number, number, number, number, number, number] => {
  const dHeight = height;
  const dWidth = width;
  const sHeight = img.height;
  const sWidth = img.width;
  const dAspectRatio = dWidth / dHeight;
  const sAspectRatio = sWidth / sHeight;
  const dh = dHeight / sHeight;
  const dw = dWidth / sWidth;

  // The destination is equal or "wider" than the source, in which case we
  // crop the top and bottom of the source image.
  if (dAspectRatio >= sAspectRatio) {
    const r = dHeight / dw;
    return [0, (sHeight - r) / 2, sWidth, r, 0, 0, dWidth, dHeight];
  }

  // The source is "wider" than the destination, in which case we crop the
  // left and right of the source image.
  else {
    const r = dWidth / dh;
    return [(sWidth - r) / 2, 0, r, sHeight, 0, 0, dWidth, dHeight];
  }
};

const ImagePreviewComponent = React.forwardRef<
  HTMLCanvasElement,
  ImagePreviewProps
>(
  (props, forwardedRef): React.ReactElement => {
    const {
      height = DEFAULT_HEIGHT,
      filter = '',
      width = DEFAULT_WIDTH,
      className,
      onLoad = noop,
      onError = noop,
      theme: _,
      file,
      ...rest
    } = props;

    const canvasRef = useCombinedRefs<HTMLCanvasElement>(forwardedRef);

    useFileReader(file, 'data-url', (err, result): void => {
      if (err) {
        onError(err);
      } else if (canvasRef.current) {
        const imgEl = document.createElement('img');
        imgEl.src = result;

        imgEl.onload = (): void => {
          if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            if (ctx) {
              ctx.clearRect(0, 0, width, height);
              ctx.filter = filter;
              (ctx as any).drawImage(imgEl, ...cropImage(props, imgEl));
              onLoad();
            }
          }
        };
      }
    });

    return (
      <canvas
        className={cx(ROOT_CLASS_NAME, className)}
        ref={canvasRef}
        height={height}
        width={width}
        {...rest}
      />
    );
  },
);

ImagePreviewComponent.displayName = 'ImagePreview';

export const ImagePreview = styled(ImagePreviewComponent)``;
